import { useState } from "react";
import { createKpiAsync, updateKpiAsync, enableKpiAsync, disableKpiAsync } from "@features/admin/kpi/services/kpiSvc";
import { CreateKpiRequest, UpdateKpiRequest } from "@features/admin/kpi/types/kpiRequests";
import { useApiError } from "@infrastructure/api/hooks/useApiError";

/**
 * Hook providing actions for creating and updating KPIs and a busy indicator.
 *
 * The hook exposes a boolean `busy` that reflects whether an async KPI action is
 * currently in progress, and two async action functions:
 * - `doKpiCreate(nationId, data)` — creates a KPI for the given nationId.
 * - `doKpiUpdate(nationId, kpiId, data)` — updates the KPI with the given kpiId.
 *
 * Both action functions set `busy` to true before invoking their corresponding
 * API helpers and set `busy` back to false after the call completes. The raw
 * responses returned by the underlying API helpers are returned to the caller.
 *
 * Note: this hook acquires a `showError` helper (via `useApiError`) for error
 * presentation, but API errors are not automatically handled inside the actions;
 * errors thrown by the API helpers will propagate to the caller. Callers should
 * handle errors or use the `showError` helper if desired.
 *
 * @returns An object containing:
 *  - `busy` boolean indicating whether an action is running.
 *  - `doKpiCreate` async function to create a KPI: (nationId: number, data: CreateKpiRequest) => Promise<unknown>.
 *  - `doKpiUpdate` async function to update a KPI: (nationId: number, kpiId: number, data: UpdateKpiRequest) => Promise<unknown>.
 *
 */
export function useKpiAction() {
    const [busy, setBusy] = useState(false);
    const _showError = useApiError();

    // KPI Create
    const doKpiCreate = async (nationId: number, data: CreateKpiRequest) => {
        setBusy(true);

        // Call the create KPI service
        const response = await createKpiAsync(nationId, data);
        setBusy(false);
        return response;

    };

    // KPI Update
    const doKpiUpdate = async (nationId: number, kpiId: number, data: UpdateKpiRequest) => {
        setBusy(true);

        // Call the update KPI service
        const response = await updateKpiAsync(nationId, kpiId, data);
        setBusy(false);
        return response;
    };

    // KPI Disable
    const doKpiDisable = async (nationId: number, kpiId: number) => {
        setBusy(true);
        const response = await disableKpiAsync(nationId, kpiId);
        setBusy(false);
        return response;
    };

    // KPI Enable
    const doKpiEnable = async (nationId: number, kpiId: number) => {
        setBusy(true);
        const response = await enableKpiAsync(nationId, kpiId);
        setBusy(false);
        return response;
    };

    return { busy, doKpiCreate, doKpiUpdate, doKpiDisable, doKpiEnable };
};